import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  Truck, 
  CheckCircle,
  Zap,
  Shield,
  Users,
  Star,
  Loader2,
  CreditCard,
  ArrowRight,
  LogOut,
  Gift
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, updateProfile, logout, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [refreshing, setRefreshing] = useState(true);
  const sessionId = searchParams.get('session_id');

  // Refresh user data on page load to check for trial status
  useEffect(() => {
    const checkUserStatus = async () => {
      setRefreshing(true);
      const updatedUser = await refreshUser();
      setRefreshing(false);
      
      // If user now has subscription or trial, redirect
      if (updatedUser?.weekly_subscription_active || updatedUser?.free_trial_active) {
        toast.success('Access granted! Redirecting to dashboard...');
        navigate('/provider');
      }
    };
    
    checkUserStatus();
  }, []);

  useEffect(() => {
    if (sessionId) {
      pollSubscriptionStatus();
    }
  }, [sessionId]);

  useEffect(() => {
    // If already subscribed OR has free trial, redirect to dashboard
    if (user?.weekly_subscription_active || user?.free_trial_active) {
      navigate('/provider');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const pollSubscriptionStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Subscription verification timed out. Please check your dashboard.');
      return;
    }

    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API_URL}/api/subscription/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        toast.success('Subscription activated! Welcome to THM.');
        // Refresh user data
        window.location.href = '/provider';
        return;
      }

      setTimeout(() => pollSubscriptionStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking subscription:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollSubscriptionStatus(attempts + 1), pollInterval);
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/subscription/create-checkout`, {
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
      setLoading(false);
    }
  };

  if (checkingStatus || refreshing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg font-medium">
            {checkingStatus ? 'Activating your subscription...' : 'Checking your access...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
              THE HOTSHOT MARKETPLACE
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:block">
              {user?.email}
            </span>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-sm mb-6">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Provider Access</span>
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            ACTIVATE YOUR
            <br />
            <span className="text-primary">PROVIDER ACCOUNT</span>
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Get unlimited access to hotshot job requests and start earning today.
          </p>
        </motion.div>

        {/* Free Trial Info Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 p-4 bg-purple-500/10 border border-purple-500/20 rounded-sm"
        >
          <div className="flex items-center gap-3">
            <Gift className="w-6 h-6 text-purple-500 shrink-0" />
            <div>
              <p className="font-medium text-purple-400">Want to try before you buy?</p>
              <p className="text-sm text-muted-foreground">
                Contact us to request a free trial period. Email: support@thehotshotmarketplace.com.au
              </p>
            </div>
          </div>
        </motion.div>

        {/* Subscription Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-md mx-auto"
        >
          <div className="bg-card border-2 border-primary rounded-sm overflow-hidden">
            <div className="bg-primary px-6 py-3">
              <p className="text-white font-semibold text-center">WEEKLY ACCESS</p>
            </div>
            
            <div className="p-8">
              <div className="text-center mb-8">
                <p className="text-5xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  A$9.95
                </p>
                <p className="text-muted-foreground">per week</p>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  { icon: Zap, text: 'Instant SMS & email job alerts' },
                  { icon: Users, text: 'Unlimited job requests to bid on' },
                  { icon: Star, text: 'Only 5 providers can quote per job' },
                  { icon: Shield, text: 'Guaranteed secure payment' },
                  { icon: CheckCircle, text: 'Build your reputation with reviews' },
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm">{item.text}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className="w-full btn-active gap-2 text-base py-6"
                onClick={handleSubscribe}
                disabled={loading}
                data-testid="subscribe-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Subscribe & Start Earning
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Cancel anytime. Service fee only charged on completed jobs.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-16 text-center"
        >
          <p className="text-sm text-muted-foreground mb-6">
            Trusted by hotshot drivers across Australia
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              No hidden fees
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Secure payments
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Cancel anytime
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
